import type {
  CashflowScenarioCode,
  CashflowScenarioParams,
  CashflowSourceTypeValue,
} from "@station/contracts";
import { Decimal } from "@station/core";

/**
 * Nakit akış ayarlarının SAF dönüşümleri. Karşılıksız çek oranı sözleşmede BAZ PUAN tam sayısıdır
 * (250 = %2,5); kullanıcıya her zaman YÜZDE gösterilir. Dönüşüm float kullanmaz: `Decimal` ile yapılır,
 * böylece 2,5 → 250 → 2,5 gidiş-dönüşünde kayma olmaz.
 */

/** Baz puan → Türkiye biçiminde yüzde metni ("250" → "2,50"). */
export function basisPointsToPercentText(basisPoints: number): string {
  return Decimal.from(String(basisPoints)).div("100").formatTr(2);
}

/**
 * Yüzde metni → baz puan. "2,5" ve "2.5" kabul edilir; boş metin 0 sayılır. Geçersiz metinde null döner
 * (form alanı hatalı işaretlenir, gövdeye bozuk değer gitmez). Sonuç 0–10.000 arasına kırpılır.
 */
export function percentTextToBasisPoints(text: string): number | null {
  const normalized = text.trim().replace(/\s/g, "").replace(",", ".");
  if (normalized === "") return 0;
  if (!/^\d{1,3}(\.\d{1,2})?$/.test(normalized)) return null;
  const basisPoints = Number(Decimal.from(normalized).mul("100").round(0).toString());
  if (!Number.isInteger(basisPoints) || basisPoints < 0 || basisPoints > 10_000) return null;
  return basisPoints;
}

/** Bir senaryonun form durumu: sayısal alanlar metin girdisidir. */
export interface ScenarioFormState {
  receivableDelayDays: string;
  chequeBouncePercent: string;
  posDelayDays: string;
}

export type ScenarioFormMap = Record<CashflowScenarioCode, ScenarioFormState>;

export interface CashflowSettingsFormState {
  horizonDays: string;
  alertScenario: CashflowScenarioCode;
  alertHorizonDays: string;
  scenarios: ScenarioFormMap;
}

export function scenarioStateFrom(params: CashflowScenarioParams): ScenarioFormState {
  return {
    receivableDelayDays: String(params.receivableDelayDays),
    chequeBouncePercent: basisPointsToPercentText(params.chequeBounceBasisPoints),
    posDelayDays: String(params.posDelayDays),
  };
}

export function settingsStateFrom(settings: {
  horizonDays: number;
  alertScenario: CashflowScenarioCode;
  alertHorizonDays: number;
  scenarios: Record<CashflowScenarioCode, CashflowScenarioParams>;
}): CashflowSettingsFormState {
  return {
    horizonDays: String(settings.horizonDays),
    alertScenario: settings.alertScenario,
    alertHorizonDays: String(settings.alertHorizonDays),
    scenarios: {
      OPTIMISTIC: scenarioStateFrom(settings.scenarios.OPTIMISTIC),
      BASE: scenarioStateFrom(settings.scenarios.BASE),
      PESSIMISTIC: scenarioStateFrom(settings.scenarios.PESSIMISTIC),
    },
  };
}

const wholeNumber = (text: string, fallback: number): number => {
  const parsed = Number(text.trim());
  return Number.isInteger(parsed) ? parsed : fallback;
};

/** Yüzdesi bozuk olan senaryoların kodları; boşsa form gönderilebilir. */
export function invalidScenarioCodes(state: CashflowSettingsFormState): CashflowScenarioCode[] {
  return (Object.keys(state.scenarios) as CashflowScenarioCode[]).filter(
    (code) => percentTextToBasisPoints(state.scenarios[code].chequeBouncePercent) === null,
  );
}

/** Form → `PATCH /cashflow/settings` gövdesi. Üç senaryo da gönderilir (uç kısmi yamayı kabul eder). */
export function toSettingsPayload(
  state: CashflowSettingsFormState,
  version: number,
): Record<string, unknown> {
  const scenarioPayload = (code: CashflowScenarioCode) => {
    const scenario = state.scenarios[code];
    return {
      receivableDelayDays: wholeNumber(scenario.receivableDelayDays, 0),
      chequeBounceBasisPoints: percentTextToBasisPoints(scenario.chequeBouncePercent) ?? 0,
      posDelayDays: wholeNumber(scenario.posDelayDays, 0),
    };
  };
  return {
    horizonDays: wholeNumber(state.horizonDays, 30),
    alertScenario: state.alertScenario,
    alertHorizonDays: wholeNumber(state.alertHorizonDays, 14),
    scenarios: {
      OPTIMISTIC: scenarioPayload("OPTIMISTIC"),
      BASE: scenarioPayload("BASE"),
      PESSIMISTIC: scenarioPayload("PESSIMISTIC"),
    },
    version,
  };
}

// ── Kalem kaynağı → ekran rotası ─────────────────────────────────────────────
/** Kaynağın açılacağı rota ve (varsa) arama parametreleri; eşleşme yoksa null (düz metin gösterilir). */
export interface CashflowSourceTarget {
  to: string;
  params?: Record<string, string>;
  search?: Record<string, string>;
}

/**
 * `sourceType` → ekran. Eşleme TAM (`Record<CashflowSourceTypeValue, …>`): sözleşmeye yeni bir kaynak türü
 * eklenirse burası derlenmez ve rota eklenmesi gerektiği anlaşılır. Ekranı olmayan tür `null` döner; arayüz
 * o zaman bağlantı yerine düz metin çizer (kırık bağlantı üretilmez).
 *
 * `counterpartyId` verilirse cariye bağlı kaynaklar (açık kalem, DBS, tekrarlayan gider) doğrudan CARİ KARTINA
 * gider — açık kalem kimliğiyle açılan bir ekran yoktur.
 */
const SOURCE_TARGETS: Record<
  CashflowSourceTypeValue,
  (sourceId: string, counterpartyId: string | null) => CashflowSourceTarget | null
> = {
  OPEN_ITEM: (_sourceId, counterpartyId) =>
    counterpartyId
      ? { to: "/cari/$id", params: { id: counterpartyId }, search: { tab: "open-items" } }
      : { to: "/cari", search: { tab: "open-items" } },
  INSTRUMENT: (sourceId) => ({ to: "/cek-senet/$id", params: { id: sourceId } }),
  POS_BATCH: () => ({ to: "/finans/pos" }),
  DBS_ITEM: () => ({ to: "/finans/dbs" }),
  LOAN_INSTALLMENT: () => ({ to: "/finans/krediler" }),
  RECURRING_EXPENSE: () => ({ to: "/giderler/tanimlar", search: { tab: "recurring" } }),
  TAX_OBLIGATION: () => ({ to: "/vergi-takvimi", search: { view: "list" } }),
  COMPANY_CARD: () => ({ to: "/giderler/tanimlar", search: { tab: "cards" } }),
  // Akaryakıt satış TAHMİNİ: belge değil, hesaplanmış kalem → satışlar ekranına götürür.
  FUEL_SALES_FORECAST: () => ({ to: "/akaryakit/satislar" }),
};

export function cashflowSourceTarget(
  sourceType: CashflowSourceTypeValue,
  sourceId: string,
  counterpartyId: string | null = null,
): CashflowSourceTarget | null {
  return SOURCE_TARGETS[sourceType]?.(sourceId, counterpartyId) ?? null;
}

/** Kalem giriş mi çıkış mı: işaretli tutarın yönünden okunur (tür değil, tutar karar verir). */
export function isInflow(signedAmount: string): boolean {
  return BigInt(signedAmount) >= 0n;
}

/** Ufuk seçenekleri; sözleşme en çok 90 gün kabul eder. */
export const HORIZON_OPTIONS = [30, 60, 90] as const;
